import { Product } from '@ordering/gql/graphql';
import { isMealDealOfferEntry, OfferTypes } from '@ordering/utils/promotions';
import { Section } from '@ordering/types/Schedule';

export type ProductTag = { name: string };

export type ProductWithTags = Product & {
  tags: ProductTag[];
};

export type SectionWithProducts = {
  id: string;
  name: string;
  displayName?: string;
  image: string | null;
  entries?: (OfferTypes.OfferEntry | ProductWithTags)[];
};

export const groupProductsBySections = (
  products: Product[],
  sections: Section[],
  activeOffers: OfferTypes.Offer[]
): SectionWithProducts[] => {
  const sectionDictionary = new Map<string, SectionWithProducts>();

  const productDictionary = new Map<string, ProductWithTags>();
  products.forEach((product) => {
    productDictionary.set(product.id, { ...product, tags: [] });
  });

  const promotionDictionary = new Map<string, OfferTypes.Offer>();
  activeOffers.forEach((promo) => {
    promotionDictionary.set(promo.offerId, promo);
  });

  sections.forEach((section) => {
    const entriesInSection: (OfferTypes.OfferEntry | ProductWithTags)[] = [];
    section.selectedEntries.forEach((selectedEntry) => {
      if ('productId' in selectedEntry) {
        const product = productDictionary.get(selectedEntry.productId);
        if (product) {
          entriesInSection.push({ ...product, tags: selectedEntry.tags ?? [] });
        }
      } else if ('offerId' in selectedEntry) {
        const offer = promotionDictionary.get(selectedEntry.offerId);

        if (offer && isMealDealOfferEntry(offer)) {
          entriesInSection.push({
            offerId: offer.offerId,
            active: offer.active,
            promotion: offer.promotion as Omit<OfferTypes.Promotion, 'availability'>,
            image: selectedEntry.image,
            isHighlighted: selectedEntry.isHighlighted,
          });
        }
      }
    });

    if (entriesInSection.length > 0) {
      sectionDictionary.set(section.id, {
        id: section.id,
        name: section.name,
        displayName: section.displayName ?? undefined,
        image: section.image || null,
        entries: entriesInSection,
      });
    }
  });

  return Array.from(sectionDictionary.values());
};
